import { NumberParam, StringParam, useQueryParams } from 'use-query-params';
import { useCallback, useEffect, useMemo } from 'react';

import { ENUM_QUERY_PARAMS, ORDER_TYPES } from '@hooks/useListViewQueryParams';
import { useExternalOrdersContext } from '@context/ExternalOrdersContext';

import {
  DEFAULT_MAX_PER_PAGE,
  DEFAULT_ORDER_BY,
  DEFAULT_ORDER_TYPE,
  EXTERNAL_ORDER_TAB,
  FIRST_PAGE,
  TAB_QUERY_PARAM,
} from '../constants';

const DEFAULT_TAB_PARAMS = {
  [ENUM_QUERY_PARAMS.CURRENT_PAGE]: FIRST_PAGE,
  [ENUM_QUERY_PARAMS.MAX_PER_PAGE]: DEFAULT_MAX_PER_PAGE,
  [ENUM_QUERY_PARAMS.ORDER_BY]: DEFAULT_ORDER_BY,
  [ENUM_QUERY_PARAMS.ORDER_TYPE]: DEFAULT_ORDER_TYPE,
};

const DEFAULT_PARAMS = {
  ...DEFAULT_TAB_PARAMS,
  [TAB_QUERY_PARAM]: EXTERNAL_ORDER_TAB.ALL,
};

const TAB_KEYS = Object.values(EXTERNAL_ORDER_TAB);

/**
 * The query string is user editable, so every param is validated before it reaches the list route:
 * an unknown tab, a page below one, a non-positive page size or a sort key that is not a sortable
 * column is dropped rather than being forwarded.
 *
 * A param the URL does not hold falls back on what the module remembers of the list — the defaults
 * on a first visit — so coming back to the bare path lands on the tab, the page and the sort the list
 * was left on instead of starting over.
 */
const normalizeParams = ({ params, persistedParams, sortableKeys }) => {
  const fallbackParams = { ...DEFAULT_PARAMS, ...persistedParams };

  const tab = TAB_KEYS.includes(params[TAB_QUERY_PARAM])
    ? params[TAB_QUERY_PARAM]
    : fallbackParams[TAB_QUERY_PARAM];

  const currentPage =
    Number.isInteger(params[ENUM_QUERY_PARAMS.CURRENT_PAGE]) &&
    params[ENUM_QUERY_PARAMS.CURRENT_PAGE] >= FIRST_PAGE
      ? params[ENUM_QUERY_PARAMS.CURRENT_PAGE]
      : fallbackParams[ENUM_QUERY_PARAMS.CURRENT_PAGE];

  const maxPerPage =
    Number.isInteger(params[ENUM_QUERY_PARAMS.MAX_PER_PAGE]) &&
    params[ENUM_QUERY_PARAMS.MAX_PER_PAGE] > 0
      ? params[ENUM_QUERY_PARAMS.MAX_PER_PAGE]
      : fallbackParams[ENUM_QUERY_PARAMS.MAX_PER_PAGE];

  const orderBy = sortableKeys.includes(params[ENUM_QUERY_PARAMS.ORDER_BY])
    ? params[ENUM_QUERY_PARAMS.ORDER_BY]
    : fallbackParams[ENUM_QUERY_PARAMS.ORDER_BY];

  const orderType = Object.values(ORDER_TYPES).includes(params[ENUM_QUERY_PARAMS.ORDER_TYPE])
    ? params[ENUM_QUERY_PARAMS.ORDER_TYPE]
    : fallbackParams[ENUM_QUERY_PARAMS.ORDER_TYPE];

  return { tab, currentPage, maxPerPage, orderBy, orderType };
};

/**
 * Tab, pagination and sort of the external orders list, held in the query string so a page can be
 * bookmarked, shared and returned to — same param names as the other paginated lists of the app
 * (`current_page`, `max_per_page`, `order_by`, `order_type`), plus the `tab` the app owns alone.
 *
 * The URL only ever holds the active tab: the pagination and the sort of the other tabs live in the
 * module context, restored when the user comes back to them. The active params are mirrored there
 * too, which is what makes a return on the bare path — through the sidebar or the breadcrumb rather
 * than through the back button of a form — land on the list as it was left. That memory dies with
 * the module: leaving it starts over on the defaults, while a reload reads the URL back.
 *
 * The five params are declared in a single `useQueryParams` rather than through the shared
 * `useListViewQueryParams`: switching tab rewrites all of them at once, and only one setter can do
 * that atomically — chained `replaceIn` calls would each rebuild the URL from a stale location.
 *
 * @param {Object} params
 * @param {Array<string>} params.sortableKeys - The `propertyKey` of the sortable columns
 *
 * @returns {Object} The normalized params and their setters
 */
export const useExternalOrdersListParams = ({ sortableKeys }) => {
  const { listParamsRef, paramsByTabRef } = useExternalOrdersContext();

  const [queryParams, setQueryParams] = useQueryParams({
    [TAB_QUERY_PARAM]: StringParam,
    [ENUM_QUERY_PARAMS.CURRENT_PAGE]: NumberParam,
    [ENUM_QUERY_PARAMS.MAX_PER_PAGE]: NumberParam,
    [ENUM_QUERY_PARAMS.ORDER_BY]: StringParam,
    [ENUM_QUERY_PARAMS.ORDER_TYPE]: StringParam,
  });

  const { tab, currentPage, maxPerPage, orderBy, orderType } = normalizeParams({
    params: queryParams,
    persistedParams: listParamsRef.current,
    sortableKeys,
  });

  const resolvedParams = useMemo(
    () => ({
      [TAB_QUERY_PARAM]: tab,
      [ENUM_QUERY_PARAMS.CURRENT_PAGE]: currentPage,
      [ENUM_QUERY_PARAMS.MAX_PER_PAGE]: maxPerPage,
      [ENUM_QUERY_PARAMS.ORDER_BY]: orderBy,
      [ENUM_QUERY_PARAMS.ORDER_TYPE]: orderType,
    }),
    [tab, currentPage, maxPerPage, orderBy, orderType],
  );

  const isUrlUpToDate = useMemo(
    () =>
      Object.keys(resolvedParams).every((param) => queryParams[param] === resolvedParams[param]),
    [resolvedParams, queryParams],
  );

  /**
   * Remember what the list is displaying, and spell it out in the URL when the URL does not say it
   * — on arrival, so it reads like the other paginated lists, and whenever the list is pushed back
   * on its bare path while it stays mounted. Nothing is resolved here: the values are the ones the
   * render already reasoned on, so the list does not query twice.
   */
  useEffect(() => {
    const { [TAB_QUERY_PARAM]: activeTab, ...activeTabParams } = resolvedParams;

    paramsByTabRef.current[activeTab] = activeTabParams;
    listParamsRef.current = resolvedParams;

    if (isUrlUpToDate) {
      return;
    }

    setQueryParams(resolvedParams, 'replaceIn');
  }, [resolvedParams, isUrlUpToDate]);

  const selectTab = useCallback(
    (nextTab) => {
      const restoredParams = paramsByTabRef.current[nextTab] || DEFAULT_TAB_PARAMS;

      setQueryParams({ ...restoredParams, [TAB_QUERY_PARAM]: nextTab }, 'replaceIn');
    },
    [setQueryParams],
  );

  const setCurrentPage = useCallback(
    (value) => {
      setQueryParams({ [ENUM_QUERY_PARAMS.CURRENT_PAGE]: value }, 'replaceIn');
    },
    [setQueryParams],
  );

  const setMaxPerPage = useCallback(
    (value) => {
      setQueryParams(
        { [ENUM_QUERY_PARAMS.MAX_PER_PAGE]: value, [ENUM_QUERY_PARAMS.CURRENT_PAGE]: FIRST_PAGE },
        'replaceIn',
      );
    },
    [setQueryParams],
  );

  const setOrderBy = useCallback(
    (value) => {
      setQueryParams({ [ENUM_QUERY_PARAMS.ORDER_BY]: value }, 'replaceIn');
    },
    [setQueryParams],
  );

  const setOrderType = useCallback(
    (value) => {
      setQueryParams({ [ENUM_QUERY_PARAMS.ORDER_TYPE]: value }, 'replaceIn');
    },
    [setQueryParams],
  );

  /**
   * Drop the memory of every tab and start the active one over. Narrowing the list — search, filters
   * or store perimeter — changes how many pages each tab holds and how they are ordered, so the
   * remembered pages would point at results that are no longer there.
   */
  const resetParamsOfAllTabs = useCallback(() => {
    paramsByTabRef.current = {};

    setQueryParams({ ...DEFAULT_TAB_PARAMS }, 'replaceIn');
  }, [setQueryParams]);

  return {
    tab,
    currentPage,
    maxPerPage,
    orderBy,
    orderType,
    selectTab,
    setCurrentPage,
    setMaxPerPage,
    setOrderBy,
    setOrderType,
    resetParamsOfAllTabs,
  };
};
